import React, { useState, useRef, useEffect } from 'react';
import { Col, Row, Form, FormGroup, Label, Input, Button, Spinner, Alert, InputGroup } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import * as Constants from '../Pages/Constants';
import { FormattedMessage } from 'react-intl';
import ESPProvisioner, { Security1 } from 'esp-ble-prov';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const isChromiumBased = /Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent) && !/OPR\//.test(navigator.userAgent);
const isAndroid = /Android/i.test(navigator.userAgent);

/**
 * Workaround for Android Chrome BLE MTU issues with esp-ble-prov.
 *
 * On Android, the default BLE MTU is 23 bytes (20 payload). The esp-ble-prov library
 * does single readValue()/writeValue() calls that can exceed this limit, causing
 * truncated protobuf responses (error "4+8 > 8" = protobuf buffer underflow).
 *
 * This function patches the provisioner instance to:
 * 1. Add a post-connect delay so Android has time to negotiate a larger MTU
 * 2. Use writeValueWithResponse() for proper ATT Long Write support
 * 3. Fetch scan results one-by-one to keep BLE response sizes small
 * 4. Retry establishSession on failure (MTU negotiation may still be in progress)
 */
function patchForAndroidBLE(provisioner) {
    if (!isAndroid) return provisioner;

    const POST_CONNECT_DELAY = 1500;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 600;

    const origConnect = provisioner.connect.bind(provisioner);
    const origWriteValue = provisioner.writeValue.bind(provisioner);
    const origEstablishSession = provisioner.establishSession.bind(provisioner);

    // 1. Post-connect delay for MTU negotiation
    provisioner.connect = async function (options) {
        const device = await origConnect(options);
        console.log('[BLE-MTU] Post-connect delay for Android MTU negotiation...');
        await new Promise(r => setTimeout(r, POST_CONNECT_DELAY));
        return device;
    };

    // 2. Use writeValueWithResponse (non-deprecated, supports ATT Long Write)
    provisioner.writeValue = async function (ep_name, data) {
        const char = provisioner.getCharacteristic(ep_name);
        if (typeof char.writeValueWithResponse === 'function') {
            return char.writeValueWithResponse(data);
        }
        return origWriteValue(ep_name, data);
    };

    // 3. Retry establishSession (key exchange responses ~56 bytes can exceed small MTU)
    provisioner.establishSession = async function () {
        let lastError;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            try {
                if (attempt > 0) {
                    console.log(`[BLE-MTU] Retrying session establishment (attempt ${attempt + 1}/${MAX_RETRIES})...`);
                    await new Promise(r => setTimeout(r, RETRY_DELAY * attempt));
                }
                return await origEstablishSession();
            } catch (err) {
                lastError = err;
                console.warn(`[BLE-MTU] establishSession attempt ${attempt + 1} failed:`, err.message);
            }
        }
        throw lastError;
    };

    // 4. Override scan to fetch results one-by-one (keeps each BLE response small)
    provisioner.scan = async function (options) {
        const scanOpts = options || { blocking: true, passive: false, groupChannels: 0, periodMs: 120 };
        await provisioner.startScan(scanOpts);
        const count = await provisioner.getScanStatus();
        const allResults = [];
        for (let i = 0; i < count; i++) {
            try {
                const results = await provisioner.getScanResults({ startIndex: i, count: 1 });
                allResults.push(...results);
            } catch (err) {
                console.warn(`[BLE-MTU] Failed to fetch scan result ${i}:`, err.message);
            }
        }
        return allResults;
    };

    console.log('[BLE-MTU] Android BLE workarounds applied.');
    return provisioner;
}

/** Detect if an error is likely caused by BLE MTU truncation */
function isMtuError(error) {
    const msg = (error && error.message) || '';
    // protobuf buffer underflow patterns: "X+Y > Z" where data was truncated
    return /\d+\+\d+\s*>\s*\d+/.test(msg) || msg.includes('index out of range');
}

/**
 * Workaround for "already paired" issue on Windows Chrome.
 *
 * When a device was previously paired, the ESP32 may retain a stale Security1
 * session. The GATT connect() succeeds instantly but establishSession() fails
 * because the ESP32's state machine is not in the initial state.
 *
 * This function forces a clean GATT disconnect/reconnect cycle (without
 * re-triggering the device picker) and re-discovers characteristics,
 * effectively resetting the ESP32's session state.
 */
async function forceReconnectBLE(provisioner) {
    const device = provisioner.device;
    const serviceUUID = provisioner.serviceUUID;

    // 1. Force GATT disconnect to reset ESP32 session state
    if (device?.gatt?.connected) {
        device.gatt.disconnect();
    }
    provisioner._isConnected = false;
    await new Promise(r => setTimeout(r, 1000));

    // 2. Reconnect GATT (reuses same device — no picker needed)
    const server = await device.gatt.connect();
    const service = await server.getPrimaryService(serviceUUID);

    // 3. Update provisioner internal state
    provisioner.server = server;
    provisioner.service = service;
    provisioner.endpoints = new Map();

    // 4. Re-discover characteristics (reproduces private discoverCharacteristics())
    const characteristics = await service.getCharacteristics();
    const decoder = new TextDecoder();
    for (const char of characteristics) {
        try {
            const descriptor = await char.getDescriptor(0x2901);
            const name = await descriptor.readValue().then(v => decoder.decode(v));
            provisioner.endpoints.set(name, char);
        } catch (err) {
            console.warn('[BLE-RECONNECT] Failed to get descriptor:', err);
        }
    }
    if (provisioner.endpoints.size === 0) {
        throw new Error('No valid endpoints discovered after reconnect');
    }
    provisioner._isConnected = true;
    console.log('[BLE-RECONNECT] Clean reconnect successful.');
}

const WifiProvisioningMenu = (props) => {
    const { classes, cancelPopup } = props;

    // Step: 'connect' (BLE pairing + WiFi scan) -> 'provision' (WiFi credentials)
    const [step, setStep] = useState('connect');

    // BLE connection state
    const provisionerRef = useRef(null);
    const [connectStatus, setConnectStatus] = useState('idle'); // idle, connecting, scanning, error
    const [isConnected, setIsConnected] = useState(false);
    const [networks, setNetworks] = useState([]);

    // Provisioning form state
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [ssid, setSsid] = useState('');
    const [passphrase, setPassphrase] = useState('');
    const [showPassphrase, setShowPassphrase] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, provisioning, success, error
    const [errorMessage, setErrorMessage] = useState('');

    // Known BLE devices (previously authorized by the browser)
    const [knownDevices, setKnownDevices] = useState([]);

    useEffect(() => {
        if (!isChromiumBased || typeof navigator.bluetooth?.getDevices !== 'function') return;
        navigator.bluetooth.getDevices()
            .then(devices => {
                // All devices returned here were granted by this app (via deviceNamePrefix:"tucky").
                // d.name may be null when the device is out of range — don't filter on it.
                console.log('[BLE] getDevices() returned:', devices.length, devices.map(d => d.name));
                setKnownDevices(devices);
            })
            .catch(err => {
                console.warn('[BLE] getDevices() failed:', err);
            });
    }, []);

    // STEP 1: Connect to gateway via BLE + scan WiFi networks
    const handleConnect = async () => {
        try {
            setConnectStatus('connecting');
            setErrorMessage('');

            let provisioner = new ESPProvisioner({
                deviceNamePrefix: "tucky",
                serviceUUID: "021a9004-0382-4aea-bff4-6b3f1c5adfb4",
                security: new Security1({ pop: "abcd1234" })
            });

            // Apply Android BLE MTU workarounds
            patchForAndroidBLE(provisioner);

            await provisioner.connect();

            // Try to establish session; on failure (stale pairing), force a clean
            // GATT reconnect with fresh Security1 state and retry once.
            try {
                await provisioner.establishSession();
            } catch (sessionError) {
                console.warn('[BLE] Session failed, forcing clean reconnect...', sessionError.message);
                await forceReconnectBLE(provisioner);
                // Reset Security1 crypto state (old instance has stale keys)
                provisioner.security = new Security1({ pop: "abcd1234" });
                await provisioner.establishSession();
            }

            provisionerRef.current = provisioner;
            setIsConnected(true);

            // Scan WiFi networks from the gateway
            setConnectStatus('scanning');
            const scanResults = await provisioner.scan();

            // Decode SSIDs, filter empty, sort by signal strength, deduplicate
            const seen = new Set();
            const decodedNetworks = scanResults
                .map(n => ({
                    ssid: textDecoder.decode(n.ssid),
                    rssi: n.rssi,
                    channel: n.channel,
                    bssid: n.bssid,
                    auth: n.auth,
                    raw: n
                }))
                .filter(n => n.ssid.length > 0)
                .sort((a, b) => b.rssi - a.rssi)
                .filter(n => {
                    if (seen.has(n.ssid)) return false;
                    seen.add(n.ssid);
                    return true;
                });

            setNetworks(decodedNetworks);
            setConnectStatus('idle');
            setStep('provision');
        } catch (error) {
            console.error("BLE connection error:", error);
            setConnectStatus('error');
            if (error.name === 'NotFoundError' || (error.message && error.message.includes('cancelled'))) {
                setErrorMessage('Device selection cancelled');
            } else if (isAndroid && isMtuError(error)) {
                setErrorMessage(
                    'Erreur de communication Bluetooth (MTU trop petit). ' +
                    'Essayez d\'activer le flag Chrome : chrome://flags/#web-bluetooth-request-larger-mtu ' +
                    'puis redémarrez Chrome.'
                );
            } else {
                setErrorMessage(error.message || 'Failed to connect to the gateway');
            }
        }
    };

    const handleNetworkSelect = (e) => {
        const selectedSsid = e.target.value;
        if (selectedSsid === '__manual__') {
            setSelectedNetwork(null);
            setSsid('');
        } else {
            const network = networks.find(n => n.ssid === selectedSsid);
            setSelectedNetwork(network || null);
            setSsid(selectedSsid);
        }
    };

    // STEP 2: Send WiFi credentials via the already-connected BLE session
    const handleProvision = async (e) => {
        e.preventDefault();

        if (!ssid) {
            setErrorMessage('SSID is required');
            setStatus('error');
            return;
        }

        const provisioner = provisionerRef.current;
        if (!provisioner) {
            setErrorMessage('Not connected to gateway');
            setStatus('error');
            return;
        }

        try {
            setStatus('provisioning');
            setErrorMessage('');

            const credentials = {
                ssid: selectedNetwork ? selectedNetwork.raw.ssid : textEncoder.encode(ssid),
                passphrase: textEncoder.encode(passphrase),
            };
            if (selectedNetwork) {
                credentials.bssid = selectedNetwork.raw.bssid;
                credentials.channel = selectedNetwork.channel;
            }

            await provisioner.sendCredentials(credentials);
            setStatus('success');
        } catch (error) {
            console.error("Provisioning error:", error);
            setStatus('error');
            setErrorMessage(error.message || 'An unknown error occurred during provisioning');
        }
    };

    const getSignalDetails = (rssi) => {
        const signalThresholds = [
            { threshold: -45, icon: '▂▄▆█', color: 'green' },
            { threshold: -55, icon: '▂▄▆░', color: 'green' },
            { threshold: -65, icon: '▂▄░░', color: 'limegreen' },
            { threshold: -75, icon: '▂░░░', color: 'orange' },
            { threshold: -85, icon: '░░░░', color: 'red' },
            { threshold: -100, icon: '✕░░░', color: 'red' },
        ];
        return signalThresholds.find(s => rssi >= s.threshold) || { icon: '✕░░░', color: 'gray' };
    };

    const handleDisconnect = () => {
        if (provisionerRef.current) {
            provisionerRef.current.disconnect().catch(() => {});
            provisionerRef.current = null;
        }
        setIsConnected(false);
        setConnectStatus('idle');
        setNetworks([]);
        setSelectedNetwork(null);
        setSsid('');
        setPassphrase('');
        setStatus('idle');
        setErrorMessage('');
    };

    // STEP 1 (direct): Connect to a previously-authorized BLE device without re-opening the picker
    const handleConnectKnown = async (device) => {
        try {
            setConnectStatus('connecting');
            setErrorMessage('');

            let provisioner = new ESPProvisioner({
                deviceNamePrefix: "tucky",
                serviceUUID: "021a9004-0382-4aea-bff4-6b3f1c5adfb4",
                security: new Security1({ pop: "abcd1234" })
            });
            patchForAndroidBLE(provisioner);

            // Bypass the device picker — connect directly to the known device
            provisioner.device = device;
            const server = await device.gatt.connect();
            const service = await server.getPrimaryService("021a9004-0382-4aea-bff4-6b3f1c5adfb4");
            provisioner.server = server;
            provisioner.service = service;
            provisioner.endpoints = new Map();
            provisioner._isConnected = true;

            const characteristics = await service.getCharacteristics();
            const decoder = new TextDecoder();
            for (const char of characteristics) {
                try {
                    const descriptor = await char.getDescriptor(0x2901);
                    const name = await descriptor.readValue().then(v => decoder.decode(v));
                    provisioner.endpoints.set(name, char);
                } catch (err) { /* skip chars without User Description */ }
            }
            if (provisioner.endpoints.size === 0) throw new Error('No valid endpoints discovered');

            try {
                await provisioner.establishSession();
            } catch (sessionError) {
                console.warn('[BLE] Session failed on known device, forcing clean reconnect...', sessionError.message);
                await forceReconnectBLE(provisioner);
                provisioner.security = new Security1({ pop: "abcd1234" });
                await provisioner.establishSession();
            }

            provisionerRef.current = provisioner;
            setIsConnected(true);
            setConnectStatus('scanning');

            const scanResults = await provisioner.scan();
            const seen = new Set();
            const decodedNetworks = scanResults
                .map(n => ({ ssid: textDecoder.decode(n.ssid), rssi: n.rssi, channel: n.channel, bssid: n.bssid, auth: n.auth, raw: n }))
                .filter(n => n.ssid.length > 0)
                .sort((a, b) => b.rssi - a.rssi)
                .filter(n => { if (seen.has(n.ssid)) return false; seen.add(n.ssid); return true; });

            setNetworks(decodedNetworks);
            setConnectStatus('idle');
            setStep('provision');
        } catch (error) {
            console.error("BLE direct connect error:", error);
            setConnectStatus('error');
            setErrorMessage(error.message || 'Failed to connect to the gateway');
        }
    };

    const handleBack = () => {
        if (provisionerRef.current) {
            provisionerRef.current.disconnect().catch(() => {});
            provisionerRef.current = null;
        }
        setIsConnected(false);
        setStep('connect');
        setConnectStatus('idle');
        setNetworks([]);
        setSelectedNetwork(null);
        setSsid('');
        setPassphrase('');
        setStatus('idle');
        setErrorMessage('');
    };

    return (
        <div className={classes.popup_background}>
            <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div className="management-menu-popup" style={{ height: 'auto', padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
                    <Row className="justify-content-md-center" style={{ paddingBottom: "20px" }}>
                        <Col style={{ fontWeight: "bold", fontSize: "1.8em", textAlign: "center" }}>
                            <FormattedMessage id="wifi.provisioning.title" defaultMessage="Configure the gateway’s wifi" />
                        </Col>
                    </Row>

                    <Row style={{ padding: "10px 20px" }}>
                        <Col>
                            {!isChromiumBased ? (
                                <Alert color="warning" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <span style={{ fontSize: '1.4em', lineHeight: 1 }}>⚠️</span>
                                    <div>
                                        <strong>
                                            <FormattedMessage id="wifi.provisioning.browser.warning.title" defaultMessage="Navigateur non compatible" />
                                        </strong>
                                        <br />
                                        <FormattedMessage
                                            id="wifi.provisioning.browser.warning.body"
                                            defaultMessage="La fonctionnalité Bluetooth (BLE) n'est disponible que sur les navigateurs basés sur Chromium. Veuillez utiliser {chrome} ou {edge} sur ordinateur ou mobile."
                                            values={{
                                                chrome: <strong>Google Chrome</strong>,
                                                edge: <strong>Microsoft Edge</strong>,
                                            }}
                                        />
                                    </div>
                                </Alert>
                            ) : (
                                <>
                                    {errorMessage && (connectStatus === 'error' || status === 'error') && (
                                        <Alert color="danger">{errorMessage}</Alert>
                                    )}

                                    {status === 'success' && (
                                        <Alert color="success">
                                            <FormattedMessage id="wifi.provisioning.success" defaultMessage="Provisioning successful! The device will now connect to the network. The led on the gateway will turn violet for a few seconds to configure and turn green when it is online." />
                                        </Alert>
                                    )}

                                    {/* STEP 1: BLE Gateway Connection */}
                                    {step === 'connect' && (
                                        <div style={{ textAlign: 'center' }}>
                                            <p style={{ marginBottom: '20px' }}>
                                                <FormattedMessage id="wifi.provisioning.connect.description" defaultMessage="Use Bluetooth to connect to the gateway and after configure the WiFi connection" />
                                            </p>

                                            {/* Known/previously-paired devices */}
                                            {knownDevices.length > 0 && (
                                                <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#f0f7ff', borderRadius: '6px', border: '1px solid #b8d8f8', textAlign: 'left' }}>
                                                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '8px' }}>
                                                        <FormattedMessage id="wifi.provisioning.knownDevices" defaultMessage="Appareil connu :" />
                                                    </div>
                                                    {knownDevices.map((d, i) => (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                                            <span style={{ fontWeight: 'bold' }}>📶 {d.name || 'Tucky Gateway'}</span>
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleConnectKnown(d)}
                                                                disabled={connectStatus === 'connecting' || connectStatus === 'scanning'}
                                                            >
                                                                {connectStatus === 'connecting' || connectStatus === 'scanning' ? (
                                                                    <><Spinner size="sm" color="light" style={{ marginRight: '6px' }} />
                                                                    <FormattedMessage id="wifi.provisioning.button.connecting" defaultMessage="Connecting..." /></>
                                                                ) : (
                                                                    <FormattedMessage id="wifi.provisioning.button.connectDirect" defaultMessage="Connecter" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                {/* Grayed-out when a known device exists */}
                                                <Button
                                                    color={knownDevices.length > 0 ? 'secondary' : 'success'}
                                                    outline={knownDevices.length > 0}
                                                    onClick={handleConnect}
                                                    disabled={connectStatus === 'connecting' || connectStatus === 'scanning'}
                                                >
                                                    {connectStatus === 'connecting' ? (
                                                        <>
                                                            <Spinner size="sm" color="light" style={{ marginRight: '10px' }} />
                                                            <FormattedMessage id="wifi.provisioning.button.connecting" defaultMessage="Connecting..." />
                                                        </>
                                                    ) : connectStatus === 'scanning' ? (
                                                        <>
                                                            <Spinner size="sm" color="light" style={{ marginRight: '10px' }} />
                                                            <FormattedMessage id="wifi.provisioning.button.scanning" defaultMessage="Scanning Wi-Fi networks..." />
                                                        </>
                                                    ) : knownDevices.length > 0 ? (
                                                        <FormattedMessage id="wifi.provisioning.button.otherGateway" defaultMessage="Autre appareil..." />
                                                    ) : (
                                                        <FormattedMessage id="wifi.provisioning.button.connect" defaultMessage="Connect to Gateway (BLE)" />
                                                    )}
                                                </Button>

                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 2: WiFi Provisioning Form */}
                                    {step === 'provision' && status !== 'success' && (
                                        <Form onSubmit={handleProvision}>
                                            {networks.length > 0 && (
                                                <FormGroup>
                                                    <Label style={{ fontWeight: 'bold' }}>
                                                        <FormattedMessage id="wifi.provisioning.selectNetwork" defaultMessage="Detected Wi-Fi Networks" />
                                                    </Label>
                                                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '4px' }}>
                                                        <div
                                                            style={{ padding: '8px 12px', cursor: status === 'provisioning' ? 'default' : 'pointer', backgroundColor: selectedNetwork === null ? '#e9ecef' : 'transparent', opacity: status === 'provisioning' ? 0.6 : 1 }}
                                                            onClick={() => { if (status !== 'provisioning') { setSelectedNetwork(null); setSsid(''); } }}
                                                        >
                                                            -- Enter SSID manually --
                                                        </div>
                                                        {networks.map((n, i) => {
                                                            const signal = getSignalDetails(n.rssi);
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    style={{ padding: '8px 12px', cursor: status === 'provisioning' ? 'default' : 'pointer', backgroundColor: selectedNetwork && selectedNetwork.ssid === n.ssid ? '#e9ecef' : 'transparent', display: 'flex', alignItems: 'center', gap: '8px', opacity: status === 'provisioning' ? 0.6 : 1 }}
                                                                    onClick={() => { if (status !== 'provisioning') { setSelectedNetwork(n); setSsid(n.ssid); } }}
                                                                >
                                                                    <span style={{ color: signal.color, fontFamily: 'monospace', fontWeight: 'bold' }}>{signal.icon}</span>
                                                                    <span>{n.ssid}</span>
                                                                    <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 'auto' }}>({n.rssi} dBm)</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </FormGroup>
                                            )}

                                            <FormGroup>
                                                <Label for="networkSSID" style={{ fontWeight: 'bold' }}>
                                                    <FormattedMessage id="wifi.provisioning.ssid" defaultMessage="Network Name (SSID)" />
                                                </Label>
                                                <Input
                                                    type="text"
                                                    id="networkSSID"
                                                    placeholder="Enter Wi-Fi network name"
                                                    value={ssid}
                                                    onChange={(e) => { setSsid(e.target.value); setSelectedNetwork(null); }}
                                                    disabled={status === 'provisioning' || selectedNetwork !== null}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label for="networkPassphrase" style={{ fontWeight: 'bold' }}>
                                                    <FormattedMessage id="wifi.provisioning.passphrase" defaultMessage="Passphrase" />
                                                </Label>
                                                <InputGroup>
                                                    <Input
                                                        type={showPassphrase ? 'text' : 'password'}
                                                        id="networkPassphrase"
                                                        placeholder="Enter Wi-Fi password"
                                                        value={passphrase}
                                                        onChange={(e) => setPassphrase(e.target.value)}
                                                        disabled={status === 'provisioning'}
                                                    />
                                                    <Button
                                                        type="button"
                                                        color="secondary"
                                                        outline
                                                        onClick={() => setShowPassphrase(v => !v)}
                                                        disabled={status === 'provisioning'}
                                                        style={{ lineHeight: 1 }}
                                                        title={showPassphrase ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                    >
                                                        {showPassphrase ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </Button>
                                                </InputGroup>
                                            </FormGroup>

                                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '10px' }}>
                                                <Button color="secondary" onClick={handleBack} disabled={status === 'provisioning'}>
                                                    <FormattedMessage id="wifi.provisioning.button.back" defaultMessage="Back" />
                                                </Button>
                                                <Button color="primary" type="submit" disabled={status === 'provisioning'}>
                                                    {status === 'provisioning' ? (
                                                        <>
                                                            <Spinner size="sm" color="light" style={{ marginRight: '10px' }} />
                                                            <FormattedMessage id="wifi.provisioning.button.loading" defaultMessage="Provisioning..." />
                                                        </>
                                                    ) : (
                                                        <FormattedMessage id="wifi.provisioning.button.start" defaultMessage="Start Provisioning" />
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>

                    <Row style={{ marginTop: '20px' }}>
                        <Col style={{ textAlign: 'center' }}>
                            <Button onClick={cancelPopup} color="secondary" className="btn btn-danger">
                                <FormattedMessage id="button.close" defaultMessage="Close" />
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default withStyles(Constants.styles)(WifiProvisioningMenu);
